import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, homeForUser } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { NauticusLogo } from "@/components/NauticusLogo";
import { SBALogo } from "@/components/SBALogo";
import { PoweredByFooter } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ArrowRight, Info } from "lucide-react";


export default function Register() {
  const { register, refresh } = useAuth();
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const resumeToken = params.get("resume");
  const [journey, setJourney] = useState(params.get("journey") || null);
  const isAssessment = params.has("resume") || params.get("brand") === "sba";
  const isLife = journey === "life";
  const sbaBrand = isAssessment && !isLife;   // Medicare/SBA branding
  const showInvite = params.has("invite");    // worksite onboarding links only; hidden from consumers
  const Brand = sbaBrand ? SBALogo : NauticusLogo;
  const loginTo = sbaBrand ? "/login?brand=sba" : "/login";
  const headingText = isLife
    ? "Save your Life & Protection review"
    : sbaBrand ? "Save your Medicare review" : "Create your account";
  const descText = isLife
    ? "Create a secure password to save your results and come back to your protection review anytime."
    : sbaBrand
      ? "Create a secure password to save your progress and continue your Medicare review."
      : "Save what you've told Nauticus so you don't have to start over.";
  const [form, setForm] = useState({ name: "", email: "", password: "", invite_code: "" });
  const [error, setError] = useState("");
  const [duplicate, setDuplicate] = useState(false);
  const [loading, setLoading] = useState(false);

  // Where a returning user should land AFTER signing in: back on their saved assessment
  // results with the resume token preserved. Login honours the safe internal `next`.
  const resultsPath = isLife ? "/life/assessment/results" : "/sba/assessment/results";
  const rawNext = params.get("next");
  const safeNext = (rawNext && rawNext.startsWith("/") && !rawNext.startsWith("//") && !rawNext.startsWith("/\\") && !rawNext.includes("://")) ? rawNext : null;
  const signInHref = resumeToken
    ? `/login?${sbaBrand ? "brand=sba&" : ""}resume=${encodeURIComponent(resumeToken)}&next=${encodeURIComponent(`${resultsPath}?resume=${resumeToken}`)}`
    : safeNext ? `/login?next=${encodeURIComponent(safeNext)}` : loginTo;

  useEffect(() => {
    if (!resumeToken) return;
    try {
      const p = JSON.parse(sessionStorage.getItem("sba_prefill_" + resumeToken) || "null");
      if (p) setForm((f) => ({ ...f, name: p.name || f.name, email: p.email || f.email }));
    } catch {}
    // Detect the journey for correct branding on direct visits (no ?journey param).
    if (!journey) {
      api.get(`/public/assessment/${encodeURIComponent(resumeToken)}/results`)
        .then((r) => setJourney(r.data?.journey_type || "medicare"))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ask-origin: carry the name confirmed in Ask so it isn't re-typed at registration.
  useEffect(() => {
    if (resumeToken) return;
    let askToken = null;
    try { askToken = localStorage.getItem("ask_session_token"); } catch { /* ignore */ }
    if (!askToken) return;
    try {
      const p = JSON.parse(sessionStorage.getItem("ask_prefill") || "null");
      if (p?.name || p?.email) setForm((f) => ({ ...f, name: f.name || p.name || "", email: f.email || p.email || "" }));
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setDuplicate(false);
    setLoading(true);
    try {
      const resume = new URLSearchParams(window.location.search).get("resume");
      let askToken = null;
      try {
        const t = localStorage.getItem("ask_session_token");
        askToken = t && localStorage.getItem("ask_save_intent") === t ? t : null;
      } catch { /* ignore */ }
      const u = await register({ ...form, resume_token: resume || undefined, ask_session_token: askToken || undefined });
      if (askToken) { try { localStorage.removeItem("ask_session_token"); localStorage.removeItem("ask_save_intent"); } catch { /* ignore */ } }
      if (resume) {
        // register already claimed the lead household; refresh session.
        await refresh();
        // Detect token type + journey: assessment tokens return to their saved
        // Intelligence Results (Medicare -> /sba, Life -> /life); legacy intake
        // tokens fall back to the general Nauticus experience.
        let resultsPath = null;
        try {
          const { data: probe } = await api.get(`/public/assessment/${encodeURIComponent(resume)}/results`);
          resultsPath = probe?.journey_type === "life" ? "/life/assessment/results" : "/sba/assessment/results";
        } catch (_) {
          resultsPath = null;
        }
        navigate(resultsPath ? `${resultsPath}?resume=${encodeURIComponent(resume)}` : "/app");
        return;
      }
      // Ask-origin registration: return to the SAME authenticated Ask conversation with the
      // active Medicare review attached — never a generic Member Home detour.
      if (askToken) {
        try { sessionStorage.removeItem("ask_prefill"); } catch { /* ignore */ }
        let toHousehold = false;
        try { toHousehold = sessionStorage.getItem("ask_after_save") === "household"; sessionStorage.removeItem("ask_after_save"); } catch { /* ignore */ }
        navigate(toHousehold ? "/app/member#my-household" : "/app/ask");
        return;
      }
      navigate(safeNext || homeForUser(u));
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(formatApiError(detail) || err.message);
      // Duplicate account: this is a returning user — offer sign-in (resume preserved), not a dead end.
      if (err.response?.status === 400 && typeof detail === "string" && detail.toLowerCase().includes("already exists")) {
        setDuplicate(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-12 bg-mist-100 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            {sbaBrand ? (
              <div className="flex flex-col leading-none" data-testid="register-sba-lockup-mobile">
                <SBALogo className="h-9 sm:h-11" />
                <span data-testid="register-sba-tagline-mobile" className="mt-2 pl-[2px] text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.24em] text-[#1E3E62]">
                  Senior Intelligence. Better Decisions.
                </span>
              </div>
            ) : (
              <Brand />
            )}
          </div>
          <h1 className="font-display text-3xl font-bold text-navy-900 tracking-tight" data-testid="register-heading">{headingText}</h1>
          <p className="text-muted-foreground mt-2 mb-8">{descText}</p>

          {error && (
            <Alert variant="destructive" className="mb-5" data-testid="register-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {duplicate && (
            <div className="mb-5 rounded-xl border-2 border-[#1E3E62] bg-[#EAF2FB] p-4" data-testid="register-duplicate-signin">
              <p className="text-sm font-bold text-[#0B192C]">You already have an account with this email.</p>
              <p className="text-sm text-[#33475B] mt-1">No need for a new email — sign in and we'll save this assessment to your account.</p>
              <Button asChild className="mt-3 h-11 px-5 w-full rounded-full bg-[#0B192C] text-white hover:bg-[#1E3E62] font-bold" data-testid="register-duplicate-signin-btn">
                <Link to={signInHref}>Sign in to save this assessment <ArrowRight className="w-4 h-4 ml-1" /></Link>
              </Button>
              <Link to="/forgot-password" className="mt-2 inline-block text-sm text-[#1E3E62] underline" data-testid="register-duplicate-forgot">Forgot your password? Reset it, then sign in — your answers stay saved.</Link>
            </div>
          )}

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="font-semibold">Full name</Label>
              <Input id="name" required value={form.name} onChange={upd("name")} readOnly={isAssessment && !!form.name}
                placeholder="Jordan Rivera" className="mt-1.5 h-11 bg-white" data-testid="register-name" />
            </div>
            <div>
              <Label htmlFor="email" className="font-semibold">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={upd("email")} readOnly={isAssessment && !!form.email}
                placeholder="you@example.com" className="mt-1.5 h-11 bg-white" data-testid="register-email" />
            </div>
            <div>
              <Label htmlFor="password" className="font-semibold">Password</Label>
              <Input id="password" type="password" required value={form.password} onChange={upd("password")}
                placeholder="At least 8 characters" className="mt-1.5 h-11 bg-white" data-testid="register-password" />
            </div>
            {showInvite && (
            <div>
              <Label htmlFor="invite" className="font-semibold">Employer invite code <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input id="invite" value={form.invite_code} onChange={upd("invite_code")}
                placeholder="e.g. HARBOR2026" className="mt-1.5 h-11 bg-white" data-testid="register-invite" />
            </div>
            )}
            <Button type="submit" disabled={loading} data-testid="register-submit"
              className="w-full h-11 bg-gold text-navy-900 hover:bg-gold-light rounded-full font-bold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{isAssessment ? "Save & continue" : "Create account"} <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </form>

          {!isAssessment && showInvite && (
          <div className="mt-5 flex items-start gap-2 text-xs text-muted-foreground">
            <Info className="w-4 h-4 text-gold-dark shrink-0 mt-0.5" />
            <span>Use invite code <span className="font-semibold">HARBOR2026</span> to join the demo employer, or leave blank to browse the demo catalog.</span>
          </div>
          )}

          <p className="text-sm text-muted-foreground mt-6">
            Already have an account?{" "}
            <Link to={signInHref} className="text-gold-dark font-semibold hover:underline" data-testid="go-login">Sign in</Link>
          </p>
          <PoweredByFooter className="mt-10" />
        </div>
      </div>

      <div className="relative hidden lg:block order-1 lg:order-2 bg-navy-800">
        <div className="absolute inset-0 nauticus-hero-overlay" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex justify-end">
            {sbaBrand ? (
              <div className="flex flex-col items-end leading-none" data-testid="register-sba-lockup-panel">
                <SBALogo light className="h-9 sm:h-11" />
                <span data-testid="register-sba-tagline-panel" className="mt-2 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.24em] text-[#EDE7D6]">
                  Senior Intelligence. Better Decisions.
                </span>
              </div>
            ) : (
              <Brand light />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
